import { referenceDataApi } from '../../api/referenceData.js';
import { Tabs } from '../../components/ui';
import { useUrlState } from '../../url';
import {
  CC_DEFAULT_COLS,
  LM_DEFAULT_COLS,
  SITE_DEFAULT_COLS,
} from '../ReferenceData/constants.js';
import { DataExplorer } from '../ReferenceData/DataExplorer.jsx';
import { TaskMappingExplorer } from '../ReferenceData/TaskMappingExplorer.jsx';
import { HolOverrideExplorer } from './HolOverrideExplorer.jsx';
import { MirrorLocationsExplorer } from './MirrorLocationsExplorer.jsx';
import { PayCodeExplorer } from './PayCodeExplorer.jsx';

// Per-location switches, frozen to the right of OT Hrs. Each is a contract
// term that varies plant to plant, so it lives per location rather than in the
// global rule book. The row keys are the merged Sites view's df.org_units
// columns (prefixed `df_`); Yes is the default everywhere and means "behave
// exactly as this location does today".
const SITE_SWITCH_COLS = [
  {
    // Stored as the negative (`ot_recalc_disabled`, DEFAULT 0) so the nightly
    // ref sync's untouched-column default already means "recalc runs" — hence
    // `invert`. Until now the No list was maintained by hand in
    // infra/migrate_org_units_ot_recalc_disabled.sql (2026-07-08); those seven
    // sites are seeded, and this switch is now the way to change it.
    key: 'df_ot_recalc_disabled',
    label: 'OT Recalc',
    invert: true,
    onSave: (siteCode, enabled) => referenceDataApi.updateOtRecalc(siteCode, enabled),
    confirm: (code, enabled) => (enabled
      ? `Run OT Recalculation at location "${code}"?\n\nFrom the next reprocess PSC's own weekly threshold decides overtime here again: Reg hours past the threshold upgrade to OVERTIME, and Dayforce OT this site's week did not earn downgrades to STRAIGHT TIME.`
      : `Skip OT Recalculation at location "${code}"?\n\nFrom the next reprocess this site bills Dayforce's own classification as-is — no Reg-to-OVERTIME upgrade and no OT-to-STRAIGHT TIME downgrade. The OT Hrs threshold stops applying here.`),
  },
  {
    key: 'df_training_billable',
    label: 'Training Billable',
    onSave: (siteCode, billable) =>
      referenceDataApi.updateSiteBillability(siteCode, 'training_billable', billable),
    confirm: (code, billable) => (billable
      ? `Bill TRAINING time at location "${code}"?\n\nTraining hours will bill at their contract rate again on the next reprocess.`
      : `Stop billing TRAINING time at location "${code}"?\n\nEvery training line at this site (including training hours Dayforce classified as overtime) bills $0 from the next reprocess.`),
  },
  {
    key: 'df_drug_screen_billable',
    label: 'Drug Screen Billable',
    onSave: (siteCode, billable) =>
      referenceDataApi.updateSiteBillability(siteCode, 'drug_screen_billable', billable),
    confirm: (code, billable) => (billable
      ? `Bill DRUG SCREENING time at location "${code}"?\n\nDrug-screening hours will bill at their contract rate again on the next reprocess.`
      : `Stop billing DRUG SCREENING time at location "${code}"?\n\nEvery drug-screening line at this site (including hours Dayforce classified as overtime) bills $0 from the next reprocess.`),
  },
];

const RULE_TABS = [
  { id: 'sites',            label: 'Locations' },
  { id: 'mirror_locations', label: 'Mirror Locations' },
  { id: 'cost_centers',     label: 'Cost Centers' },
  { id: 'labor_metrics',    label: 'Labor Metrics' },
  { id: 'task_mapping',     label: 'Task Mapping' },
  { id: 'pay_code',         label: 'Pay Code Rules' },
  { id: 'hol_override',     label: 'Pay Code Overrides' },
];

const RULE_TAB_IDS = new Set(RULE_TABS.map((t) => t.id));
const DEFAULT_RULE_TAB = 'sites';

export default function Rules() {
  const { segments, navigate } = useUrlState();

  // The sub-tab is the second path segment: /rules/pay_code. An unknown or
  // missing one falls back to Locations without rewriting the URL — the tab
  // bar shows where you are either way, and a redirect here would fight with
  // Back navigation.
  const requested = segments[1];
  const tab = RULE_TAB_IDS.has(requested) ? requested : DEFAULT_RULE_TAB;

  // A sub-tab change drops the previous tab's filters (they name columns that
  // don't exist on the next table) and pushes, so Back returns to the tab the
  // user came from.
  const setTab = (id) => navigate(['rules', id === DEFAULT_RULE_TAB ? null : id]);

  return (
    <div>
      <div className="section-title">Rules</div>
      <Tabs tabs={RULE_TABS} activeId={tab} onChange={setTab} />

      {/* display:none keeps each explorer's state mounted across tab switches.
          ``urlActive`` is what stops the five hidden explorers from all
          claiming the same ?search=/?cols= as the visible one. */}
      <div style={{ display: tab === 'sites' ? 'block' : 'none' }}>
        <DataExplorer
          urlActive={tab === 'sites'}
          fetchData={referenceDataApi.getMergedSites}
          exportDataset="sites"
          idKey="site_code"
          idLabel="XRef Code"
          defaultCols={SITE_DEFAULT_COLS}
          matchFilter
          toggleTable="sites"
          activeFilter
          editableNumberCol={{
            key: 'df_weekly_ot_threshold',
            label: 'OT Hrs',
            min: 1,
            max: 168,
            onSave: referenceDataApi.updateOtThreshold,
          }}
          editableBoolCols={SITE_SWITCH_COLS}
        />
      </div>
      {/* Read-only — mounted lazily; the derived pairs are a single small fetch. */}
      {tab === 'mirror_locations' && <MirrorLocationsExplorer />}
      <div style={{ display: tab === 'cost_centers' ? 'block' : 'none' }}>
        <DataExplorer
          urlActive={tab === 'cost_centers'}
          fetchData={referenceDataApi.getCostCenters}
          exportDataset="cost_centers"
          idKey="dept_id"
          idLabel="NS ID"
          defaultCols={CC_DEFAULT_COLS}
          toggleTable="cost_centers"
          activeFilter
        />
      </div>
      <div style={{ display: tab === 'labor_metrics' ? 'block' : 'none' }}>
        <DataExplorer
          urlActive={tab === 'labor_metrics'}
          fetchData={referenceDataApi.getLaborMetrics}
          exportDataset="labor_metrics"
          idKey="code"
          idLabel="XRef Code"
          defaultCols={LM_DEFAULT_COLS}
          toggleTable="labor_metrics"
        />
      </div>
      <div style={{ display: tab === 'task_mapping' ? 'block' : 'none' }}>
        <TaskMappingExplorer urlActive={tab === 'task_mapping'} />
      </div>
      <div style={{ display: tab === 'pay_code' ? 'block' : 'none' }}>
        <PayCodeExplorer urlActive={tab === 'pay_code'} />
      </div>
      <div style={{ display: tab === 'hol_override' ? 'block' : 'none' }}>
        <HolOverrideExplorer urlActive={tab === 'hol_override'} />
      </div>
    </div>
  );
}
